import { motion } from "framer-motion";
import { Star, Quote, User } from "lucide-react";

interface Testimonial {
  quote: string;
  author: string;
  company: string;
  role: string;
  rating: number;
  savings?: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "I can't imagine shopping for PEO without this ever again. We saved $20k and got better service, and I didn't have to stop my day job to do it.",
    author: "CFO",
    company: "5 Employee Tech Startup",
    role: "Chief Financial Officer",
    rating: 5,
    savings: "$20k saved"
  },
  {
    quote: "Our relationship with our PEO has given us the ability to access their expertise in HR, current labor issues, workers' comp, unemployment insurance, and most of all compliance... Our PEO is a very valuable resource.",
    author: "Business Manager",
    company: "73 Employee Professional Services Firm",
    role: "Business Manager",
    rating: 5,
    savings: "HR expertise access"
  },
  {
    quote: "I can't imagine running our business without a PEO.",
    author: "CEO",
    company: "30 Employee Tech Company",
    role: "Chief Executive Officer",
    rating: 5,
    savings: "Business confidence"
  }
];

const TestimonialCard = ({ testimonial, delay = 0 }: { testimonial: Testimonial; delay?: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="group bg-white rounded-xl shadow-soft p-6 hover:shadow-elevation-2 transition-all duration-300 cursor-pointer relative overflow-hidden"
      whileHover={{ scale: 1.02, y: -5 }}
    >
      {/* Hover reveal overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
      <div className="relative z-10">
      <div className="flex items-center gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      
      <Quote className="h-8 w-8 text-primary/20 mb-4" />
      
      <p className="text-gray-700 mb-6 leading-relaxed">
        "{testimonial.quote}"
      </p>
      
      {testimonial.savings && (
        <div className="bg-primary/10 text-primary text-sm font-semibold px-3 py-2 rounded-full mb-4 inline-block">
          {testimonial.savings}
        </div>
      )}
      
        <div className="flex items-center">
          <motion.div 
            className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4 group-hover:bg-primary/20 transition-colors" 
            aria-label="Anonymous reviewer"
            whileHover={{ scale: 1.1 }}
          >
            <User className="h-6 w-6 text-primary" />
          </motion.div>
          <div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{testimonial.role}</span> · <span className="text-primary font-medium">{testimonial.company}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const TestimonialsSection = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold mb-4">Why Organizations Outperform with PEOs:</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            NAPEO 2024 research shows organizations consistently outperform across multiple key dimensions. Find the right PEO to access these proven advantages.
          </p>
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">500+</div>
              <div className="text-sm text-gray-600">PEOs Available</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">85%</div>
              <div className="text-sm text-gray-600">Expect Growth</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">88%</div>
              <div className="text-sm text-gray-600">Express Interest</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              testimonial={testimonial}
              delay={index * 0.1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};